import { useEffect, useState } from "react";
import { useRole } from "@/contexts/RoleContext";
import { teacherHeatmap } from "@/lib/api";
import { Card } from "@/components/ui/card";

const TIER_STYLES = {
  strong:           { bg: "bg-emerald-200",  border: "border-emerald-300", text: "text-emerald-900", label: "Strong" },
  developing:       { bg: "bg-emerald-100",  border: "border-emerald-200", text: "text-emerald-800", label: "Developing" },
  early:            { bg: "bg-sky-100",      border: "border-sky-200",     text: "text-sky-800",     label: "Early" },
  needs_attention:  { bg: "bg-amber-200",    border: "border-amber-300",   text: "text-amber-900",   label: "Needs attention" },
  untouched:        { bg: "bg-slate-100",    border: "border-slate-200",   text: "text-slate-500",   label: "Untouched" },
};

export default function TeacherHeatmap() {
  const { teacherId } = useRole();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(null);
  const [threshold, setThreshold] = useState(""); // empty = default

  useEffect(() => {
    teacherHeatmap(teacherId, filter, { attention_threshold: threshold === "" ? null : Number(threshold) }).then(setData);
  }, [teacherId, filter, threshold]);

  if (!data) return <div className="text-center py-20 text-slate-400" data-testid="heatmap-loading">Loading heatmap…</div>;

  // Group by subject → topic
  const grouped = {};
  for (const r of data.rows) {
    const subj = r.subject;
    const topic = r.topic || "Other";
    grouped[subj] = grouped[subj] || {};
    grouped[subj][topic] = grouped[subj][topic] || [];
    grouped[subj][topic].push(r);
  }

  return (
    <div className="teacher-scope min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="teacher-heatmap">
        <h1 className="font-teacher text-2xl md:text-3xl font-bold text-slate-900 mb-1">Class progress heatmap</h1>
        <p className="text-slate-600 mb-6">Across {data.students} student{data.students !== 1 ? "s" : ""}. Tap a cell for the sub-topic detail.</p>

        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="subject-filters">
          <FilterChip label="All" active={!filter} onClick={() => setFilter(null)} testId="filter-all" />
          {data.subjects.map(s => (
            <FilterChip key={s} label={s} active={filter === s} onClick={() => setFilter(s)} testId={`filter-${s}`} />
          ))}
          <div className="ml-auto flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5" data-testid="attention-threshold-control">
            <label htmlFor="att-th" className="text-xs font-semibold text-slate-600 whitespace-nowrap">Alert when stops ≥</label>
            <input
              id="att-th"
              type="number"
              min="1"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              placeholder={String(data.attention_threshold)}
              className="w-16 px-2 py-1 text-sm rounded border border-slate-200 focus:border-slate-400 focus:outline-none"
              data-testid="attention-threshold-input"
            />
            <span className="text-[10px] text-slate-400">default {Math.max(1, Math.floor(data.students/3))}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-6 text-xs" data-testid="heatmap-legend">
          {Object.entries(TIER_STYLES).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2">
              <div className={`w-4 h-4 rounded ${v.bg} border ${v.border}`} />
              <span className="text-slate-600">{v.label}</span>
            </div>
          ))}
        </div>

        {Object.entries(grouped).map(([subject, topics]) => (
          <div key={subject} className="mb-8" data-testid={`subject-section-${subject}`}>
            <h2 className="font-teacher text-xl font-bold text-slate-900 mb-3">{subject}</h2>
            {Object.entries(topics).map(([topic, items]) => (
              <div key={topic} className="mb-4">
                <h3 className="text-xs uppercase font-semibold text-slate-500 tracking-wider mb-2">{topic}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {items.map(r => {
                    const tier = TIER_STYLES[r.tier] || TIER_STYLES.untouched;
                    return (
                      <Card
                        key={`${r.subject}-${r.sub_topic}`}
                        data-testid={`heat-cell-${r.sub_topic}`}
                        className={`rounded-lg p-3 border-2 ${tier.bg} ${tier.border}`}
                      >
                        <div className={`font-teacher text-sm font-semibold ${tier.text} leading-tight`}>{r.sub_topic}</div>
                        <div className="flex items-center justify-between mt-2 text-xs">
                          <span className={`font-bold ${tier.text}`}>{r.mastery_pct}% mastered</span>
                          {r.stop_count > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">{r.stop_count} stop{r.stop_count > 1 ? "s" : ""}</span>}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1">
                          {r.established} ✓ • {r.in_progress} in progress • {r.touched}/{data.students} touched
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function FilterChip({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors tap-44 ${active ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"}`}
    >
      {label}
    </button>
  );
}
